/**
 * A agenda: mês a mês, o que cada operadora enviou — e o par que faltou.
 *
 * É a primeira visão do sistema orientada ao que **não** chegou. Todas as
 * outras contam o que entrou; esta cobra o que era esperado.
 *
 * Deliberadamente modesta nesta versão, como o cliente definiu: ela alerta
 * **apenas quando um par ficou incompleto** — veio o demonstrativo de
 * pagamento e não veio o extrato de glosa, ou o contrário. Meses em que nada
 * chegou ficam quietos, porque o sistema ainda não sabe o que cada operadora
 * deveria mandar nem com que frequência. Inventar essa expectativa produziria
 * alarme falso todo mês, e alarme falso treina as pessoas a ignorar alarme.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { visaoAgenda } from '../api/fluxfin';
import { Alerta, Badge, LoadingSpinner, Painel, Vazio } from './common';
import { brl, formatDate } from '../utils/formato';

const PAPEL = {
  recebimento: 'Recebimento',
  glosa: 'Glosa',
  recebimento_e_glosa: 'Recebimento e glosa',
};

/* A ordem importa: recebido antes de glosado, que é como se lê o resultado
   — primeiro o que entrou, depois o que foi recusado. */
const PAPEIS_EXIBIDOS = [
  { chave: 'recebimento', rotulo: 'Recebido', cor: undefined },
  { chave: 'glosa', rotulo: 'Glosado', cor: 'var(--ff-perigo, #b42318)' },
  { chave: 'recebimento_e_glosa', rotulo: 'Recebido e glosado', cor: undefined },
];

export default function AgendaRecebimentos() {
  const navigate = useNavigate();
  const [linhas, setLinhas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    visaoAgenda()
      .then((r) => setLinhas(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar a agenda.')))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <LoadingSpinner />;

  const incompletos = linhas.filter((l) => l.faltando?.length);

  // Agrupa por competência: é assim que se olha o calendário.
  const meses = [];
  linhas.forEach((l) => {
    const ultimo = meses[meses.length - 1];
    if (ultimo && ultimo.competencia === l.competencia) ultimo.itens.push(l);
    else meses.push({ competencia: l.competencia, itens: [l] });
  });

  return (
    <Painel
      titulo="Agenda de recebimentos"
      subtitulo="Mês a mês, o que cada operadora enviou — e o que ficou faltando."
      acoes={
        <div className="d-flex align-items-center gap-2">
          {incompletos.length > 0 ? (
            <Badge tom="pendente">
              {incompletos.length} envio{incompletos.length === 1 ? '' : 's'} incompleto
              {incompletos.length === 1 ? '' : 's'}
            </Badge>
          ) : (
            linhas.length > 0 && <Badge tom="confirmado">Nada pendente</Badge>
          )}
          {/* Aqui cabe o resumo dos últimos meses; o mapa inteiro, com
              filtro por operadora e por tipo de arquivo, vive em tela
              própria. */}
          <button
            type="button"
            className="ff-btn ff-btn--secundario ff-btn--pequeno"
            onClick={() => navigate('/agenda')}
          >
            Ver agenda completa
          </button>
        </div>
      }
    >
      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {linhas.length === 0 ? (
        <Vazio
          icone="bi-calendar3"
          titulo="Nenhum recebimento ainda"
          descricao="Assim que o primeiro arquivo for processado, ele aparece aqui na competência dele."
        />
      ) : (
        <div className="p-3 d-flex flex-column gap-3">
          {meses.map((mes) => (
            <div key={mes.competencia}>
              <div
                className="ff-mono"
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: 'var(--ff-muted)',
                  marginBottom: 6,
                }}
              >
                {mes.competencia}
              </div>

              <div className="d-flex flex-column gap-2">
                {mes.itens.map((l) => (
                  <div
                    key={`${l.competencia}-${l.id_operadora}`}
                    style={{
                      border: '1px solid var(--ff-border-soft)',
                      borderLeft: `3px solid ${
                        l.faltando?.length
                          ? '#d9a520'
                          : l.completo
                            ? 'var(--ff-green)'
                            : 'var(--ff-border)'
                      }`,
                      borderRadius: 10,
                      padding: '10px 14px',
                    }}
                  >
                    <div className="d-flex flex-wrap align-items-center gap-3">
                      <strong style={{ flex: 1, minWidth: 160 }}>{l.operadora}</strong>

                      {/* Os valores vão SEPARADOS e rotulados. Somados, como
                          estavam, produziam um número sem significado:
                          R$ 92.343,64 recebidos mais R$ 656,37 recusados dão
                          R$ 93.000,01, que não é dinheiro que entrou nem
                          dinheiro que deixou de entrar. */}
                      {PAPEIS_EXIBIDOS.map(({ chave, rotulo, cor }) => {
                        const d = l.por_papel?.[chave];
                        if (!d) return null;
                        return (
                          <div key={chave} style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: 'var(--ff-muted)' }}>
                              {rotulo}
                              {d.qtd > 1 ? ` · ${d.qtd} arquivos` : ''}
                            </div>
                            <div className="ff-mono" style={{ fontWeight: 600, color: cor }}>
                              {brl(d.total)}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {l.arquivos.map((a) => (
                        <button
                          key={a.id_arquivo}
                          type="button"
                          className="ff-btn ff-btn--secundario ff-btn--pequeno"
                          onClick={() => navigate(`/protocolos?arquivo=${a.id_arquivo}`)}
                          title={`${PAPEL[a.papel] || a.papel} · ${brl(a.valor)} · recebido em ${formatDate(a.recebido_em)}`}
                        >
                          <i
                            className={`bi bi-${
                              a.papel === 'glosa' ? 'scissors' : 'file-earmark-text'
                            }`}
                            aria-hidden="true"
                          />
                          {a.nome}
                        </button>
                      ))}
                    </div>

                    {l.faltando?.length > 0 && (
                      <div
                        className="mt-2"
                        style={{ fontSize: 13, color: '#9a5b06' }}
                      >
                        <i className="bi bi-exclamation-triangle me-1" aria-hidden="true" />
                        Falta o arquivo de{' '}
                        <strong>
                          {l.faltando.map((p) => PAPEL[p] || p).join(' e ')}
                        </strong>
                        {l.conjunto ? ` (conjunto "${l.conjunto}")` : ''}. Sem ele, a
                        glosa desta competência fica sem explicação item a item.
                      </div>
                    )}

                    {/* O terceiro estado. Antes, a ausência de conjunto caía
                        no mesmo "completo" de quem conferiu e fechou — o
                        sistema afirmava completude a partir da própria
                        ignorância. Agora ela se nomeia, e diz o que fazer. */}
                    {l.sem_expectativa && (
                      <div className="mt-2" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                        <i className="bi bi-question-circle me-1" aria-hidden="true" />
                        Sem conjunto de arquivos cadastrado para esta operadora — o
                        sistema não sabe o que esperar, então não há como afirmar que
                        o envio está completo.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <p style={{ fontSize: 12, color: 'var(--ff-muted)', margin: '2px' }}>
            A cobrança só acontece quando a operadora tem um conjunto de arquivos
            cadastrado — é ele que diz o que se espera. Meses sem nenhum envio não
            aparecem: o sistema ainda não sabe a periodicidade de cada operadora, e
            avisar por suposição seria alarme falso.
          </p>
        </div>
      )}
    </Painel>
  );
}
